import i18n from "../i18n";
import { PII_PDF_LABELS_JA } from "./piiPdfStaticLabelsJa";
import moment from "moment";
import "moment/locale/ja";
import "moment/locale/de";
import "moment/locale/nl";
import "moment/locale/zh-cn";
import "moment/locale/ko";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { buildJapanesePiiPdfCanvas } from "./piiReportPdfCanvasJa";
/** Blob download works after async; pdf.save() is often blocked without user gesture */
function triggerPdfDownload(pdf, fileName) {
  try {
    const blob = pdf.output("blob");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 250);
  } catch (e) {
    pdf.save(fileName);
  }
}

function escapeHtml(s) {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function momentLocaleFromLang(lang) {
  const lng = (lang || "en").toLowerCase();
  if (isJapaneseLang(lang)) return "ja";
  if (lng.startsWith("zh")) return "zh-cn";
  if (lng.startsWith("ko")) return "ko";
  if (lng.startsWith("de")) return "de";
  if (lng.startsWith("nl")) return "nl";
  return "en";
}

function isJapaneseLang(lang) {
  const l = (lang || "").toLowerCase();
  return l.startsWith("ja") || l === "jpn" || l === "jp";
}

function needsRasterForLang(lang) {
  const lng = (lang || "").toLowerCase();
  return (
    isJapaneseLang(lang) ||
    lng.startsWith("zh") ||
    lng.startsWith("ko")
  );
}

async function ensureCjkFontForPdf(lang) {
  const lng = (lang || i18n.language || "en").toLowerCase();
  let id;
  let href;
  let family;
  if (isJapaneseLang(lang)) {
    id = "flyins-noto-sans-jp-pdf-font";
    href =
      "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap";
    family = '"Noto Sans JP"';
  } else if (lng.startsWith("ko")) {
    id = "flyins-noto-sans-kr-pdf-font";
    href =
      "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700&display=swap";
    family = '"Noto Sans KR"';
  } else if (lng.startsWith("zh")) {
    id = "flyins-noto-sans-sc-pdf-font";
    href =
      "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;700&display=swap";
    family = '"Noto Sans SC"';
  } else {
    id = "flyins-noto-sans-jp-pdf-font";
    href =
      "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap";
    family = '"Noto Sans JP"';
  }
  if (!document.getElementById(id)) {
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
  await new Promise((r) => setTimeout(r, 400));
  await document.fonts.ready.catch(() => {});
  try {
    await document.fonts.load(`700 16px ${family}`);
    await document.fonts.load(`400 11px ${family}`);
  } catch (e) {
    /* ignore */
  }
  return family;
}

/** Table-row layout so html2canvas clone renders correctly (flex often breaks in clone) */
function buildRowHtml(label, value, labelColor, valueColor) {
  return `<tr><td style="width:130px;padding:2px 8px 8px 0;vertical-align:top;font-size:11px;line-height:1.45;color:${labelColor};font-weight:400">${label}</td><td style="padding:2px 0 8px 0;font-size:11px;line-height:1.45;color:${valueColor};word-break:break-word">${value}</td></tr>`;
}

function buildSectionTitleHtml(text) {
  return `<tr><td colspan="2" style="padding-top:18px;padding-bottom:6px;font-size:13px;font-weight:700;color:#495057">${escapeHtml(
    text
  )}</td></tr>`;
}

/**
 * Generate PII report PDF; labels follow current i18n language.
 * Japanese/Chinese/Korean use HTML+canvas (Helvetica cannot render CJK).
 * @param {object} opts.forceVectorEn - If true, skip raster and use English vector PDF (fallback).
 */
function pdfLocaleFromLang(lang) {
  const lng = (lang || "en").toLowerCase();
  if (isJapaneseLang(lang)) return "ja-JP";
  if (lng.startsWith("zh")) return "zh-CN";
  if (lng.startsWith("ko")) return "ko";
  if (lng.startsWith("de")) return "de";
  if (lng.startsWith("nl")) return "nl";
  return "en-US";
}

function googleFontHrefForRasterLang(lang) {
  const l = (lang || "").toLowerCase();
  if (l.startsWith("ko")) {
    return "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700&display=swap";
  }
  if (l.startsWith("zh")) {
    return "https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;700&display=swap";
  }
  return "https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap";
}

async function tryHtml2canvasCapture(el) {
  const base = {
    backgroundColor: "#ffffff",
    useCORS: true,
    allowTaint: true,
    logging: false,
    onclone(clonedDoc) {
      const node = clonedDoc.getElementById("pii-pdf-capture-root");
      if (node) {
        node.style.width = "720px";
        node.style.minHeight = "520px";
        node.style.boxSizing = "border-box";
      }
    },
  };
  for (const scale of [1.35, 1, 1.65, 2]) {
    try {
      const c = await html2canvas(el, { ...base, scale });
      if (c && c.width >= 80 && c.height >= 80) return c;
    } catch (_) {
      /* next */
    }
  }
  try {
    const c = await html2canvas(el, {
      ...base,
      scale: 1.25,
      foreignObjectRendering: true,
    });
    if (c && c.width >= 80 && c.height >= 80) return c;
  } catch (_) {
    /* ignore */
  }
  return null;
}

/**
 * Element must be painted (in viewport). Off-screen / below-fold often yields 0×0 canvas.
 */
async function capturePiiPdfInViewport(innerHtml) {
  const overlay = document.createElement("div");
  overlay.setAttribute("aria-hidden", "true");
  overlay.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483640",
    "background:#f8f9fa",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "padding:16px",
    "padding-top:max(16px,8vh)",
    "overflow-y:auto",
    "pointer-events:all",
    "box-sizing:border-box",
  ].join(";");
  const msg = document.createElement("div");
  msg.style.cssText =
    "font:15px system-ui,-apple-system,sans-serif;color:#333;margin-bottom:16px;text-align:center";
  msg.textContent = "Preparing PDF…";
  overlay.appendChild(msg);

  const host = document.createElement("div");
  host.style.cssText =
    "width:720px;max-width:100%;flex-shrink:0;background:#fff;box-shadow:0 1px 8px rgba(0,0,0,0.08)";
  host.innerHTML = innerHtml;
  overlay.appendChild(host);
  document.body.appendChild(overlay);

  const root =
    host.querySelector("#pii-pdf-capture-root") || host.firstElementChild;

  let canvas = null;
  try {
    await document.fonts.ready.catch(() => {});
    await new Promise((r) => setTimeout(r, 450));
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    void root.offsetHeight;
    void root.getBoundingClientRect();
    canvas = await tryHtml2canvasCapture(root);
  } finally {
    if (overlay.parentNode) {
      document.body.removeChild(overlay);
    }
  }
  return canvas;
}

/** Iframe in viewport — some environments only paint visible frames. */
async function capturePiiPdfViaIframe(innerBodyHtml, notoHref) {
  const shell = document.createElement("div");
  shell.setAttribute("aria-hidden", "true");
  shell.style.cssText =
    "position:fixed;left:0;top:0;width:100%;height:100%;z-index:2147483639;background:#fff;pointer-events:none";
  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:absolute;left:16px;top:16px;width:720px;height:2200px;border:0;background:#fff";
  shell.appendChild(iframe);
  document.body.appendChild(shell);
  const doc = iframe.contentDocument;
  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"/><link rel="stylesheet" href="${notoHref}"/><style>html,body{margin:0;padding:0;background:#fff;width:720px;}</style></head><body>${innerBodyHtml}</body></html>`
  );
  doc.close();
  await new Promise((r) => setTimeout(r, 1200));
  await doc.fonts.ready.catch(() => {});
  await new Promise((r) => requestAnimationFrame(r));
  const root = doc.getElementById("pii-pdf-capture-root");
  let canvas = null;
  try {
    if (root) {
      canvas = await tryHtml2canvasCapture(root);
    }
  } finally {
    document.body.removeChild(shell);
  }
  return canvas;
}

export async function generatePiiReportPdf(row, opts = {}) {
  const rawLang = String(opts.language ?? i18n.language ?? "en-US");
  const forceVectorEn = opts.forceVectorEn === true;
  const locale = forceVectorEn ? "en-US" : pdfLocaleFromLang(rawLang);
  const isJa = isJapaneseLang(rawLang);

  if (typeof i18n.loadLanguages === "function" && !forceVectorEn) {
    await i18n.loadLanguages(locale).catch(() => {});
  }

  const translate = (key) => {
    if (forceVectorEn) {
      return i18n.t(key, { lng: "en-US", ns: "translation", defaultValue: key });
    }
    if (isJa && PII_PDF_LABELS_JA[key] !== undefined) {
      return PII_PDF_LABELS_JA[key];
    }
    return i18n.t(key, { lng: locale, ns: "translation", defaultValue: key });
  };

  const ml = forceVectorEn ? "en" : momentLocaleFromLang(rawLang);
  moment.locale(ml);
  try {

  const customerInfo = row.customerInfo || {};
  const deviceDetails = row.deviceDetails || {};

  const normalizeText = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value).trim();
    if (!text || text.toLowerCase() === "undefined") return "";
    if (text.toLowerCase() === "undefined undefined") return "";
    return text;
  };

  const formatAddress = (value) => {
    const normalized = normalizeText(value);
    if (!normalized) return [];
    return normalized.split(",").map((p) => p.trim()).filter(Boolean);
  };

  const em = translate("piiPdf_emDash");
  const safeValue = (value) => escapeHtml(normalizeText(value) || em);

  const tradeInDate = row.tradeInDate
    ? moment(row.tradeInDate).format("LLL")
    : em;
  const timeAgo = row.tradeInDate ? moment(row.tradeInDate).fromNow() : "";
  const tradeInDateLine = row.tradeInDate
    ? `${escapeHtml(tradeInDate)} — ${escapeHtml(timeAgo)}`
    : em;

  const addressParts = formatAddress(customerInfo.address);
  const addressHtml =
    addressParts.length > 0
      ? addressParts.map((p) => escapeHtml(p)).join("<br/>")
      : em;

  const labelC = "#6C757D";
  const valueC = "#212529";

  if (needsRasterForLang(rawLang) && !forceVectorEn) {
    const tradeInDateLinePlain = row.tradeInDate
      ? `${moment(row.tradeInDate).format("LLL")} — ${moment(row.tradeInDate).fromNow()}`
      : em;

    /* Japanese: native Canvas (no html2canvas — avoids broken capture / popups). */
    if (isJapaneseLang(rawLang)) {
      await document.fonts.ready.catch(() => {});
      await new Promise((r) => setTimeout(r, 80));
      const canvas = buildJapanesePiiPdfCanvas({
        translate,
        normalizeText,
        formatAddress,
        em,
        customerInfo,
        deviceDetails,
        row,
        tradeInDateLinePlain,
      });
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const maxW = pageWidth - 2 * margin;
      const maxH = pageHeight - 2 * margin;
      let imgW = maxW;
      let imgH = (canvas.height * imgW) / canvas.width;
      if (imgH > maxH) {
        imgH = maxH;
        imgW = (canvas.width * imgH) / canvas.height;
      }
      let jpegData;
      try {
        jpegData = canvas.toDataURL("image/jpeg", 0.92);
      } catch (err) {
        throw new Error(
          "Could not build PDF image. Try disabling browser extensions that block canvas export."
        );
      }
      pdf.addImage(jpegData, "JPEG", margin, margin, imgW, imgH);
      const fileName = `PII_Report_${row.tradeInId || row.imei || "report"}_${moment().format("YYYYMMDD")}.pdf`;
      triggerPdfDownload(pdf, fileName);
      return { englishFallback: false };
    }

    const cjkFontFamily = await ensureCjkFontForPdf(rawLang);
    const notoHref = googleFontHrefForRasterLang(rawLang);

    const logoHtml = `<span style="font-size:18px;font-weight:700;color:#192129">Flyins</span>`;

    const inner = `
<div id="pii-pdf-capture-root" style="font-family:${cjkFontFamily},'Hiragino Sans',Meiryo,sans-serif;padding:16px;color:#212529;box-sizing:border-box;width:720px;min-height:520px;background:#ffffff;">
  <div style="border-bottom:1px solid #c8c8c8;padding-bottom:12px;margin-bottom:12px;">
    <table style="width:100%;border:0;border-collapse:collapse;">
      <tr>
        <td style="width:50%;vertical-align:middle;">${logoHtml}</td>
        <td style="width:50%;text-align:right;vertical-align:middle;font-size:10px;color:#adb5bd;">${escapeHtml(
      translate("piiPdf_reportTitle")
    )}</td>
      </tr>
    </table>
  </div>
  <table style="width:100%;border:0;border-collapse:collapse;font-size:11px;">
  ${buildSectionTitleHtml(translate("piiPdf_personalInformation"))}
  ${buildRowHtml(
    translate("piiPdf_name"),
    safeValue(customerInfo.fullName),
    labelC,
    valueC
  )}
  ${buildRowHtml(translate("piiPdf_address"), addressHtml, labelC, valueC)}
  ${buildRowHtml(
    translate("piiPdf_occupation"),
    safeValue(customerInfo.occupation),
    labelC,
    valueC
  )}
  ${buildRowHtml(
    translate("piiPdf_contactNo"),
    safeValue(customerInfo.contactNo),
    labelC,
    valueC
  )}
  ${buildRowHtml(
    translate("piiPdf_emailAddress"),
    safeValue(customerInfo.email),
    labelC,
    valueC
  )}
  ${buildSectionTitleHtml(translate("piiPdf_tradeInInformation"))}
  ${buildRowHtml(
    translate("piiPdf_tradeInId"),
    safeValue(row.tradeInId),
    labelC,
    valueC
  )}
  ${buildRowHtml(translate("piiPdf_tradeInDate"), tradeInDateLine, labelC, valueC)}
  ${buildSectionTitleHtml(translate("piiPdf_deviceInformation"))}
  ${buildRowHtml(
    translate("piiPdf_deviceName"),
    safeValue(row.device),
    labelC,
    valueC
  )}
  ${buildRowHtml(
    translate("piiPdf_imei"),
    safeValue(deviceDetails.imei || row.imei),
    labelC,
    valueC
  )}
  ${buildRowHtml(
    translate("piiPdf_manufacturer"),
    safeValue(deviceDetails.manufacturer),
    labelC,
    valueC
  )}
  ${buildRowHtml(
    translate("piiPdf_storage"),
    safeValue(deviceDetails.storage),
    labelC,
    valueC
  )}
  <tr><td colspan="2" style="padding-top:20px;border-top:1px solid #c8c8c8;"></td></tr>
  </table>
</div>`;

    let canvas = await capturePiiPdfInViewport(inner);

    if (!canvas || !canvas.width || !canvas.height) {
      const iframeCanvas = await capturePiiPdfViaIframe(inner, notoHref);
      if (
        iframeCanvas &&
        iframeCanvas.width > 0 &&
        iframeCanvas.height > 0
      ) {
        canvas = iframeCanvas;
      }
    }

    if (!canvas || !canvas.width || !canvas.height) {
      if (isJapaneseLang(rawLang)) {
        throw new Error(
          "Japanese report could not be rendered. Try again or use another browser."
        );
      }
      return generatePiiReportPdf(row, {
        ...opts,
        forceVectorEn: true,
        language: rawLang,
      });
    }

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const maxW = pageWidth - 2 * margin;
    const maxH = pageHeight - 2 * margin;
    let imgW = maxW;
    let imgH = (canvas.height * imgW) / canvas.width;
    if (imgH > maxH) {
      imgH = maxH;
      imgW = (canvas.width * imgH) / canvas.height;
    }
    /* JPEG avoids jsPDF PNG decode issues on some canvas outputs */
    const jpegData = canvas.toDataURL("image/jpeg", 0.92);
    pdf.addImage(jpegData, "JPEG", margin, margin, imgW, imgH);
    const fileName = `PII_Report_${row.tradeInId || row.imei || "report"}_${moment().format("YYYYMMDD")}.pdf`;
    triggerPdfDownload(pdf, fileName);
    return { englishFallback: false };
  }

  /* Vector PDF (Latin / European scripts) */
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  pdf.setFontSize(16);
  pdf.setTextColor(25, 37, 41);
  pdf.setFont("helvetica", "bold");
  pdf.text("Flyins", margin, yPos + 7);
  const logoHeight = 12;

  pdf.setFontSize(10);
  pdf.setTextColor(173, 181, 189);
  pdf.setFont("helvetica", "normal");
  pdf.text(translate("piiPdf_reportTitle"), pageWidth - margin, yPos + logoHeight / 2, {
    align: "right",
  });

  yPos += logoHeight + 5;
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 15;

  const labelX = margin;
  const valueX = margin + 42;
  const lineH = 8;

  pdf.setFontSize(12);
  pdf.setTextColor(73, 80, 87);
  pdf.setFont("helvetica", "bold");
  pdf.text(translate("piiPdf_personalInformation"), margin, yPos);
  yPos += 10;

  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");

  const drawField = (labelKey, valueText) => {
    pdf.setTextColor(108, 117, 125);
    pdf.text(translate(labelKey), labelX, yPos);
    pdf.setTextColor(33, 37, 41);
    const lines = pdf.splitTextToSize(
      valueText || em,
      pageWidth - valueX - margin
    );
    lines.forEach((line, i) => {
      pdf.text(line, valueX, yPos + i * lineH);
    });
    yPos += Math.max(lineH, lines.length * lineH);
  };

  drawField("piiPdf_name", normalizeText(customerInfo.fullName));
  const addrParts = formatAddress(customerInfo.address);
  if (addrParts.length > 0) {
    pdf.setTextColor(108, 117, 125);
    pdf.text(translate("piiPdf_address"), labelX, yPos);
    pdf.setTextColor(33, 37, 41);
    addrParts.forEach((part) => {
      pdf.text(part, valueX, yPos);
      yPos += lineH;
    });
  } else {
    drawField("piiPdf_address", "");
  }
  drawField("piiPdf_occupation", normalizeText(customerInfo.occupation));
  drawField("piiPdf_contactNo", normalizeText(customerInfo.contactNo));
  drawField("piiPdf_emailAddress", normalizeText(customerInfo.email));
  yPos += 7;

  pdf.setFontSize(12);
  pdf.setTextColor(73, 80, 87);
  pdf.setFont("helvetica", "bold");
  pdf.text(translate("piiPdf_tradeInInformation"), margin, yPos);
  yPos += 10;
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");

  drawField("piiPdf_tradeInId", normalizeText(row.tradeInId));
  const dateStr = row.tradeInDate
    ? `${moment(row.tradeInDate).format("LLL")} — ${moment(row.tradeInDate).fromNow()}`
    : em;
  drawField("piiPdf_tradeInDate", dateStr);
  yPos += 7;

  pdf.setFontSize(12);
  pdf.setTextColor(73, 80, 87);
  pdf.setFont("helvetica", "bold");
  pdf.text(translate("piiPdf_deviceInformation"), margin, yPos);
  yPos += 10;
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");

  drawField("piiPdf_deviceName", normalizeText(row.device));
  drawField(
    "piiPdf_imei",
    normalizeText(deviceDetails.imei || row.imei)
  );
  drawField("piiPdf_manufacturer", normalizeText(deviceDetails.manufacturer));
  drawField("piiPdf_storage", normalizeText(deviceDetails.storage));

  yPos = pageHeight - margin;
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos, pageWidth - margin, yPos);

  const fileName = `PII_Report_${row.tradeInId || row.imei || "report"}_${moment().format("YYYYMMDD")}.pdf`;
  triggerPdfDownload(pdf, fileName);
  return { englishFallback: forceVectorEn };
  } finally {
    moment.locale("en");
  }
}
